<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif; background: #f4f6f9; min-height: 100vh;">
    <div class="max-w-7xl mx-auto px-4 pt-24 pb-6">
      
      <!-- HEADER -->
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-500 transition-colors mb-2 group">
            <v-icon small color="inherit" class="group-hover:-translate-x-1 transition-transform">mdi-arrow-left</v-icon>
            <span class="text-[10px] font-bold uppercase tracking-widest">Voltar para listagem</span>
          </button>
          <h1 class="text-2xl font-black text-slate-800 uppercase tracking-tight">{{ id ? 'Editar Licitação' : 'Nova Licitação' }}</h1>
        </div>
        <div v-if="id" class="flex flex-col items-end">
            <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">CÓDIGO DO PROCESSO</span>
            <div class="px-4 py-2 bg-[#333333] rounded-lg shadow-sm border border-slate-700">
              <span class="text-lg font-black text-orange-500 font-mono">#{{ id }}</span>
            </div>
        </div>
      </div>

      <div class="space-y-6 pb-24">
        <!-- TABS DE NAVEGAÇÃO -->
        <div v-if="id" class="bg-white rounded-xl p-1.5 border border-slate-200 shadow-sm flex flex-wrap md:flex-nowrap gap-1">
           <button 
             v-for="(label, i) in ['DADOS ESTRUTURAIS', 'DOTAÇÃO', 'ITENS / PROPOSTAS', 'CONTRATOS']" 
             :key="i"
             @click="tab = i"
             :class="[
               'px-6 py-3 text-[10px] font-bold uppercase tracking-widest transition-all rounded-lg flex-1 flex items-center justify-center gap-2',
               tab === i ? 'bg-[#333333] text-white shadow-md' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
             ]"
           >
             <v-icon size="16" :color="tab === i ? 'orange' : 'slate-300'">{{ ['mdi-file-document-edit', 'mdi-cash-multiple', 'mdi-trophy-outline', 'mdi-handshake-outline'][i] }}</v-icon>
             <span>{{ label }}</span>
           </button>
        </div>

        <v-tabs-items v-model="tab" class="bg-transparent">
          <!-- TAB 0: DADOS DA LICITAÇÃO -->
          <v-tab-item :key="0">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
              
              <!-- COLUNA PRINCIPAL -->
              <div class="lg:col-span-8 space-y-6">
                <!-- INFORMAÇÕES DO CERTAME -->
                <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
                  <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
                    <v-icon color="orange" small>mdi-file-document-outline</v-icon>
                    <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">Informações do Certame</h2>
                  </div>
                  
                  <div class="p-6">
                    <v-form ref="form" v-model="valid" lazy-validation>
                      <v-row dense>
                        <v-col cols="12">
                          <label class="field-label">PREFEITURA RESPONSÁVEL</label>
                          <v-autocomplete v-model="data.prefecture_id" :items="prefectures" item-value="prefecture_id" item-text="prefecture_social_name" outlined dense hide-details class="field-input font-bold" :rules="fieldRules" />
                        </v-col>

                        <v-col cols="12" md="6">
                          <label class="field-label">NÚMERO DO PROCESSO</label>
                          <v-text-field v-model="data.bidding_process_number" placeholder="EX: 001/2024" outlined dense hide-details class="field-input font-bold" :rules="fieldRules" />
                        </v-col>

                        <v-col cols="12" md="6">
                          <label class="field-label">PUBLICAÇÃO DO EDITAL</label>
                          <v-menu v-model="menus.publication" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field 
                                v-model="data.bidding_publication_notice" 
                                v-mask-date.br 
                                placeholder="DD/MM/AAAA" 
                                outlined dense hide-details 
                                class="field-input"
                                v-bind="attrs"
                                v-on="on"
                                prepend-inner-icon="mdi-calendar"
                              />
                            </template>
                            <v-date-picker 
                              :value="formatToPicker(data.bidding_publication_notice)" 
                              @input="data.bidding_publication_notice = formatFromPicker($event); menus.publication = false" 
                              no-title scrollable locale="pt-br" color="orange" 
                            />
                          </v-menu>
                        </v-col>

                        <v-col cols="12" md="6">
                          <label class="field-label">TIPO DE LICITAÇÃO</label>
                          <v-autocomplete v-model="data.type_id" :items="types" item-value="type_id" item-text="type_desc" outlined dense hide-details class="field-input" :rules="fieldRules" />
                        </v-col>

                        <v-col cols="12" md="6">
                          <label class="field-label">MODALIDADE</label>
                          <v-autocomplete v-model="data.modality_id" :items="modalities" item-value="modality_id" item-text="modality_desc" outlined dense hide-details class="field-input" :rules="fieldRules" />
                        </v-col>

                        <v-col cols="12">
                          <label class="field-label">OBJETO DA LICITAÇÃO</label>
                          <v-textarea v-model="data.bidding_object" placeholder="DESCREVA O OBJETO DE FORMA DETALHADA..." outlined dense hide-details class="field-input uppercase" rows="4" />
                        </v-col>
                      </v-row>
                    </v-form>
                  </div>
                </section>

                <!-- CRONOGRAMA -->
                <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
                  <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
                    <v-icon color="orange" small>mdi-calendar-range</v-icon>
                    <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">Cronograma de Eventos</h2>
                  </div>
                  
                  <div class="p-6">
                    <v-row dense>
                      <v-col cols="12" md="4">
                        <label class="field-label">HABILITAÇÃO</label>
                        <v-menu v-model="menus.qualification" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field 
                              v-model="data.bidding_qualification" 
                              v-mask-date.br 
                              placeholder="DD/MM/AAAA" 
                              outlined dense hide-details 
                              class="field-input"
                              v-bind="attrs"
                              v-on="on"
                              prepend-inner-icon="mdi-calendar"
                            />
                          </template>
                          <v-date-picker 
                            :value="formatToPicker(data.bidding_qualification)" 
                            @input="data.bidding_qualification = formatFromPicker($event); menus.qualification = false" 
                            no-title scrollable locale="pt-br" color="orange" 
                          />
                        </v-menu>
                      </v-col>
                      <v-col cols="12" md="4">
                        <label class="field-label">JULGAMENTO</label>
                        <v-menu v-model="menus.tenders" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field 
                              v-model="data.bidding_tenders" 
                              v-mask-date.br 
                              placeholder="DD/MM/AAAA" 
                              outlined dense hide-details 
                              class="field-input"
                              v-bind="attrs"
                              v-on="on"
                              prepend-inner-icon="mdi-calendar"
                            />
                          </template>
                          <v-date-picker 
                            :value="formatToPicker(data.bidding_tenders)" 
                            @input="data.bidding_tenders = formatFromPicker($event); menus.tenders = false" 
                            no-title scrollable locale="pt-br" color="orange" 
                          />
                        </v-menu>
                      </v-col>
                      <v-col cols="12" md="4">
                        <label class="field-label">HOMOLOGAÇÃO</label>
                        <v-menu v-model="menus.homologation" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field 
                              v-model="data.bidding_homologation" 
                              v-mask-date.br 
                              placeholder="DD/MM/AAAA" 
                              outlined dense hide-details 
                              class="field-input"
                              v-bind="attrs"
                              v-on="on"
                              prepend-inner-icon="mdi-calendar"
                            />
                          </template>
                          <v-date-picker 
                            :value="formatToPicker(data.bidding_homologation)" 
                            @input="data.bidding_homologation = formatFromPicker($event); menus.homologation = false" 
                            no-title scrollable locale="pt-br" color="orange" 
                          />
                        </v-menu>
                      </v-col>
                    </v-row>
                  </div>
                </section>
              </div>

              <!-- COLUNA LATERAL -->
              <div class="lg:col-span-4 space-y-6">
                <!-- VALORES -->
                <section class="bg-[#333333] rounded-xl overflow-hidden shadow-md border border-slate-700">
                  <div class="p-6 space-y-6">
                    <div>
                      <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">VALOR GLOBAL ESTIMADO</label>
                      <div class="flex items-center gap-3 bg-white/5 p-4 rounded-lg border border-white/5 group hover:border-orange-500/30 transition-all">
                        <span class="text-orange-500 font-black text-xl">R$</span>
                        <v-text-field v-model="data.bidding_value" v-mask-decimal.br="2" placeholder="0,00" solo flat dense hide-details class="field-input-dark font-black text-2xl" />
                      </div>
                    </div>

                    <div>
                      <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">PROPOSTAS RECEBIDAS</label>
                      <div class="flex items-center gap-3 bg-white/5 p-4 rounded-lg border border-white/5 group hover:border-orange-500/30 transition-all">
                        <v-icon color="slate-500" small>mdi-numeric</v-icon>
                        <v-text-field v-model="data.bidding_number_of_proposals" v-mask-number placeholder="0" solo flat dense hide-details class="field-input-dark font-bold text-xl" />
                      </div>
                    </div>
                  </div>
                </section>

                <!-- STATUS -->
                <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div class="p-4">
                    <button 
                      @click="data.bidding_status = !data.bidding_status"
                      type="button"
                      :class="['w-full h-16 rounded-lg border flex items-center justify-between px-6 transition-all', 
                        data.bidding_status ? 'bg-green-50 border-green-100' : 'bg-slate-50 border-slate-200']"
                    >
                      <div class="flex items-center gap-3 text-left">
                        <v-icon :color="data.bidding_status ? 'green' : 'slate-300'" small>{{ data.bidding_status ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                        <div>
                          <span :class="['text-[11px] font-bold uppercase tracking-widest block', data.bidding_status ? 'text-green-700' : 'text-slate-500']">Status</span>
                          <span class="text-[9px] font-bold text-slate-400 uppercase">{{ data.bidding_status ? 'ATIVO' : 'INATIVO' }}</span>
                        </div>
                      </div>
                      <v-switch v-model="data.bidding_status" color="green" hide-details class="mt-0" readonly />
                    </button>
                  </div>
                </section>

                <!-- AÇÕES -->
                <div class="flex flex-col gap-2">
                  <button @click="validate" :disabled="loader" class="w-full bg-[#f37021] hover:bg-orange-600 active:scale-95 text-white font-bold text-[11px] h-14 rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 uppercase tracking-widest group">
                    <v-icon v-if="!loader" size="18" color="white">mdi-check</v-icon>
                    <v-icon v-else size="18" class="animate-spin" color="white">mdi-loading</v-icon>
                    <span>{{ id ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR LICITAÇÃO' }}</span>
                  </button>
                  <button @click="$router.push('/biddings-manager')" class="w-full text-slate-400 hover:text-slate-600 font-bold text-[11px] uppercase tracking-widest py-3 transition-colors">CANCELAR</button>
                </div>
              </div>
            </div>
          </v-tab-item>

          <!-- OUTRAS ABAS (Componentes externos) -->
          <v-tab-item :key="1">
            <Donations :bidding_id="id" :prefecture_id="data.prefecture_id" />
          </v-tab-item>
          <v-tab-item :key="2">
            <Items :bidding_id="id" />
          </v-tab-item>
          <v-tab-item :key="3">
            <Contracts :bidding_id="id" />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>

    <!-- NOTIFICAÇÕES -->
    <v-snackbar v-model="snack.show" :color="snack.color" timeout="4000" top elevation="24" class="custom-snack">
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import Donations from "./donnations";
import Items from "./items";
import Contracts from "./contracts";
export default {
  props: ["id"],
  components: { Donations, Items, Contracts },
  data: () => ({
    tab: 0,
    valid: false,
    loader: false,
    prefectures: [],
    modalities: [],
    types: [],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: { show: false, message: "", color: "success" },
    menus: {
      publication: false,
      qualification: false,
      tenders: false,
      homologation: false
    },
    data: {
      modality_id: null,
      prefecture_id: null,
      type_id: null,
      bidding_id: null,
      bidding_process_number: "",
      bidding_publication_notice: "",
      bidding_qualification: "",
      bidding_tenders: "",
      bidding_homologation: "",
      bidding_value: 0,
      bidding_number_of_proposals: 0,
      bidding_object: "",
      bidding_status: false,
    },
  }),
  created() {
    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    this.$http.get("/biddings/modalities").then((r) => (this.modalities = r.data));
    this.$http.get("/biddings/types").then((r) => (this.types = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`biddings/${this.id}`).then((r) => {
        this.data = r.data[0];
        if (this.data.bidding_homologation) this.data.bidding_homologation = this.convertDate(this.data.bidding_homologation);
        if (this.data.bidding_tenders) this.data.bidding_tenders = this.convertDate(this.data.bidding_tenders);
        if (this.data.bidding_qualification) this.data.bidding_qualification = this.convertDate(this.data.bidding_qualification);
        if (this.data.bidding_publication_notice) this.data.bidding_publication_notice = this.convertDate(this.data.bidding_publication_notice);
        if (this.data.bidding_value !== null && this.data.bidding_value !== undefined && this.data.bidding_value !== '') {
          this.data.bidding_value = Number(this.data.bidding_value).toFixed(2);
        }
        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    formatToPicker(v) {
      if (!v || v.length < 10) return null;
      const [day, month, year] = v.split("/");
      return `${year}-${month}-${day}`;
    },
    formatFromPicker(v) {
      if (!v) return "";
      const [year, month, day] = v.split("-");
      return `${day}/${month}/${year}`;
    },
    validate() {
      if (this.$refs.form.validate()) this.createOrUpdate();
      else {
        this.snack.show = true; this.snack.color = "warning"; this.snack.message = "Atenção: um ou mais campos precisam ser preenchidos!"; this.loader = false;
      }
    },
    convertDate(v) {
      if (v) {
        v = v.split('T')[0].replace(/-/g, "");
        return `${v.substring(6, 8)}/${v.substring(4, 6)}/${v.substring(0, 4)}`;
      }
      return "";
    },
    createOrUpdate() {
      this.loader = true;
      this.$store.dispatch("setLoader", true);
      const method = this.id ? 'put' : 'post';
      const url = this.id ? `/biddings/${this.id}` : '/biddings';
      this.$http[method](url, this.data).then((r) => {
        this.snack.show = true; this.snack.message = r.data.result || "Sucesso!"; this.snack.color = "success";
        setTimeout(() => this.$router.push("/biddings-manager"), 1500);
      }).catch((e) => {
        this.snack.show = true; this.snack.message = e.message; this.snack.color = "error";
        this.loader = false; this.$store.dispatch("setLoader", false);
      });
    },
  },
};
</script>

<style scoped>
.field-label {
  display: block;
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.field-input >>> .v-input__control .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 6px !important;
}

.field-input >>> .v-input__control .v-input__slot fieldset {
  border-color: #e2e8f0 !important;
  border-width: 1px !important;
}

.field-input >>> .v-input__control .v-input__slot:hover fieldset {
  border-color: #f37021 !important;
}

.field-input >>> input,
.field-input >>> .v-select__selection {
  font-size: 13px !important;
  color: #334155 !important;
}

.field-input-dark >>> .v-input__control .v-input__slot {
  background: transparent !important;
}

.field-input-dark >>> input {
  color: #ffffff !important;
}

.custom-snack >>> .v-snack__wrapper {
  border-radius: 10px !important;
}

@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.animate-spin { animation: spin 1s linear infinite; }
</style>
